/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { ThemeColorScheme } from '@utils/theme';

interface ThemePreviewProps {
    theme: ThemeColorScheme;
    isSelected: boolean;
    onClick: () => void;
}

/**
 * ThemePreview component that shows a preview of a theme with sample UI elements
 */
export function ThemePreview({ theme, isSelected, onClick }: ThemePreviewProps) {
    return (
        <button
            type="button"
            aria-pressed={isSelected}
            className={`group w-full cursor-pointer rounded-lg border p-3 text-left transition duration-200 active:scale-[0.99] ${
                isSelected
                    ? 'border-theme-active bg-theme-active/10 ring-theme-focus/15 ring-2'
                    : 'border-theme-border bg-theme-content-bg hover:border-theme-active/50 hover:bg-theme-hover'
            }`}
            onClick={onClick}
        >
            <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="text-theme-text text-sm font-semibold">{theme.name}</span>
                    <span
                        className={`material-symbols-rounded text-lg transition ${
                            isSelected ? 'text-theme-active opacity-100' : 'text-theme-text-muted opacity-0'
                        }`}
                        aria-hidden="true"
                    >
                        check_circle
                    </span>
                </div>
                <div
                    className="overflow-hidden rounded-md border shadow-sm"
                    style={{
                        backgroundColor: theme.colors.bg,
                        borderColor: theme.colors.border,
                    }}
                >
                    <div
                        className="flex items-center justify-between px-3 py-2"
                        style={{
                            backgroundColor: theme.colors.nav,
                            borderBottomColor: theme.colors['border-divider'],
                            borderBottomWidth: 1,
                        }}
                    >
                        <div className="flex items-center space-x-2">
                            <div
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: theme.colors.avatar }}
                            ></div>
                            <div className="h-2 w-12 rounded" style={{ backgroundColor: theme.colors.text }}></div>
                        </div>
                        <div className="h-2 w-2 rounded" style={{ backgroundColor: theme.colors.icon }}></div>
                    </div>

                    <div className="space-y-2 p-2.5">
                        <div className="h-2 w-full rounded" style={{ backgroundColor: theme.colors.text }}></div>
                        <div
                            className="h-2 w-3/4 rounded"
                            style={{ backgroundColor: theme.colors['text-light'] }}
                        ></div>

                        <div className="mt-3">
                            <div className="h-5 w-14 rounded" style={{ backgroundColor: theme.colors.button }}></div>
                        </div>

                        <div
                            className="mt-2 rounded-md border p-2"
                            style={{
                                backgroundColor: theme.colors['card-bg'],
                                borderColor: theme.colors.border,
                            }}
                        >
                            <div
                                className="h-1.5 w-full rounded"
                                style={{ backgroundColor: theme.colors['text-muted'] }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </button>
    );
}

/**
 * Simple circular toggle switch with horizontal sliding animation.
 * @param checked Whether the switch is on
 * @param onChange Callback when the switch is toggled
 */
export function Switch({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            tabIndex={0}
            onClick={() => onChange(!checked)}
            className={`border-theme-border relative inline-flex h-7 w-12 items-center rounded-full border transition-colors duration-300 ${
                checked ? 'bg-theme-active' : 'bg-theme-button-secondary'
            }`}
        >
            <span
                className={`border-theme-border absolute top-0.5 left-0.5 h-6 w-6 rounded-full border bg-white shadow-sm transition-transform duration-300 ${
                    checked ? 'translate-x-5' : 'translate-x-0'
                }`}
            />
            <span className="sr-only">Toggle system theme following</span>
        </button>
    );
}

/**
 * ThemeSelector component combines theme previews and a system theme switch for reusability.
 * @param themes Array of theme color schemes
 * @param selectedTheme Currently selected theme key
 * @param onThemeChange Handler for theme selection
 * @param autoMode Whether to follow system theme
 * @param onAutoModeChange Handler for system theme toggle
 * @param followSystemLabel Label for the system theme switch
 * @param themeColorLabel Label for the theme color section
 */
export function ThemeSelector({
    themes,
    selectedTheme,
    onThemeChange,
    autoMode,
    onAutoModeChange,
    followSystemLabel,
    themeColorLabel,
}: {
    themes: ThemeColorScheme[];
    selectedTheme: string;
    onThemeChange: (themeKey: string) => void;
    autoMode: boolean;
    onAutoModeChange: (checked: boolean) => void;
    followSystemLabel: string;
    themeColorLabel: string;
}) {
    return (
        <div className="space-y-4">
            <div className="border-theme-border bg-theme-content-bg-light flex items-center justify-between gap-4 rounded-lg border px-3.5 py-3">
                <span className="text-theme-text text-sm font-semibold">{followSystemLabel}</span>
                <Switch checked={autoMode} onChange={onAutoModeChange} />
            </div>
            <div>
                <h3 className="sr-only">{themeColorLabel}</h3>
                <div className="grid grid-cols-2 gap-3">
                    {themes.map((theme) => (
                        <ThemePreview
                            key={theme.mode}
                            theme={theme}
                            isSelected={selectedTheme === theme.mode}
                            onClick={() => onThemeChange(theme.mode)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
